import { useEffect, useMemo, useRef, useState } from 'react'
import { videoStoryboard } from '@/config/prompts'
import { getVideoMode } from '@/config/videoModes'
import { templateTitle, type AnyVideoTemplate } from '@/config/videoTemplates'
import { Button, Drawer } from './ui'

/**
 * 模板预览 —— 卡片上那颗播放键点开的抽屉。**两种形态，同一个壳**：
 *
 * 1. **有片就播片**（`public/templates/<id>.mp4`）。这段片子是拿
 *    **用户点下这条模板后真正会发出去的那份提示词**跑出来的（同一个
 *    `buildVideoPrompt`，见 `scripts/export-template-videos.ts`），
 *    所以它演的就是"这条模板这么拍会得到什么"。片源与封面九宫格也同源 ——
 *    封面上的产品和视频里的必须是同一个东西，否则卡片与样片当场对不上。
 *    ⚠️ **自存模板播的是它自己的成片**（`sampleUrl`，用户存模板那一刻本页最近那条
 *    真实产出，用户口径「内容是生成的视频」）。它与上面那条**不是一回事**：
 *    那条是我们做的案例片，这条是用户自己跑出来的东西 —— 判据是「有没有
 *    `sampleUrl`」，见下面约束 4。
 * 2. **没有案例片就回落「分镜逐格播」**。新建的模板、还没出片时播放键不能是死的，
 *    这时按分镜时间轴逐格放封面九宫格的对应格子，播的仍然是"这条片子会怎么拍"。
 *
 * 生成/更新案例片：`scripts/export-template-videos.ts`（出清单）
 * → `scripts/gen-template-videos.py submit / fetch`（出片并落到 `public/templates/`）。
 *
 * ---------------------------------------------------------------------------
 * 硬约束
 * ---------------------------------------------------------------------------
 * 1. **帧列表的真源是 `videoStoryboard()`**，与视频提示词同一份，这里不重算时间轴。
 *    多镜头模板出它自己的 `shots`；三段式（商品视频那四条）由时长切出
 *    「2 秒 / 60% / 余下」三段。自己再算一遍就会出现「预览说 6 镜、提示词写 5 镜」。
 *    播案例片时，右侧分镜列表**用同一份帧列表去 seek**（第 i 条分镜 ↔ 视频第 i 段），
 *    所以画面跳的位置和列表说的位置天然一致。
 *
 * 2. **第 i 帧 ↔ 封面九宫格的第 i 格**。封面提示词（`scripts/template-assets.json`）
 *    是把模板分镜**按顺序**铺进前 N 格的，第 N+1..9 格是同片的补充细节帧。
 *    所以播放只走前 N 格，多出来的格子只让卡面九宫格不空 —— 点不动，也不参与播放。
 *
 * 3. **换模板要回到第一帧重播**。抽屉是同一个实例复用的，不重置的话第二条模板会
 *    从上一段的第 4 帧开始放。
 *
 * 4. **有没有案例片是探出来的，不是在源码里列出来的**。片子在 `public/templates/`
 *    下由脚本批量生成，什么时候多一条、少一条源码都不知道；写死一份 `hasVideo` 清单
 *    就是"同一份事实两处各写一遍"。探的时候要**连 content-type 一起判** ——
 *    静态托管把 `index.html` 当兜底返回时，缺文件也是 200。
 *    ⚠️ **自存模板不走这条探活**：它的片子是用户自己那条任务的产出
 *    （`sampleUrl`，产出收尾时已镜像到 /static），地址本身就是"存在"的证据。
 *    对它发 HEAD 反而有两处不对：一是多一次请求，二是**探的文件根本不对**
 *    （下面的 `videoSrc` 已经不是 `/templates/<id>.mp4` 了）。
 *    所以判据是「有没有 `sampleUrl`」—— 有就直接播，`<video onError>`
 *    仍是第二道网（文件被清掉时回落到逐格播分镜）。
 */

/** 封面是 3 行 3 列的分镜拼图（`scripts/template-assets.json` 统一按 3x3 生成） */
const COLS = 3
const CELLS = COLS * COLS

/**
 * 第 i 格（0 基）在封面图上的定位。
 *
 * 舞台是 `background-size: 300% 300%`：图被放大到容器的 3 倍。`background-position`
 * 的百分比算的是「图与容器的差」，差是 2W，所以**每格的步长是 50%**。
 * 照 3 等分写 33.3% 会错位半格 —— 这是这块最容易写错的一处。
 */
function cellStyle(cover: string, i: number) {
  return {
    backgroundImage: `url(${cover})`,
    backgroundPosition: `${(i % COLS) * 50}% ${Math.floor(i / COLS) * 50}%`,
  }
}

/** 帧时长（毫秒）。`t` 形如 `0-2.5s`；解析不出来就按时长均分兜底 */
function frameMs(t: string, fallback: number): number {
  const m = /^([\d.]+)\s*-\s*([\d.]+)\s*s$/.exec(t.trim())
  if (!m) return fallback
  const span = (parseFloat(m[2]) - parseFloat(m[1])) * 1000
  return span > 200 ? span : fallback
}

/** 镜头起始秒（`0-2.5s` → 0）—— 点分镜时把视频 seek 到这里 */
function startSec(t: string): number {
  const m = /^([\d.]+)\s*-/.exec(t.trim())
  return m ? parseFloat(m[1]) : 0
}

/** `9:16` → `9 / 16`（CSS `aspect-ratio` 的写法）；认不出来按竖版兜底 */
function ratioCss(r: string): string {
  const m = /^(\d+)\s*:\s*(\d+)$/.exec(r)
  return m ? `${m[1]} / ${m[2]}` : '9 / 16'
}

/**
 * `9:16` → `0.5625`（宽 / 高）。**只给 CSS 用** —— 播放器要按「高度上限 × 比例」
 * 反推宽度（见 `global.css` 的 `.tprev__video`），而 `aspect-ratio` 那个分数形式
 * 没法直接参与 `calc()`。认不出来按竖版兜底，与 `ratioCss` 同一套回落。
 */
function ratioNum(r: string): number {
  const m = /^(\d+)\s*:\s*(\d+)$/.exec(r)
  const h = m ? Number(m[2]) : 0
  return m && h > 0 ? Number(m[1]) / h : 9 / 16
}

export function TemplatePreview({
  tpl,
  onClose,
  onUse,
  onGenerateSample,
  engineName,
}: {
  /** 为 null 时整个抽屉不渲染 —— 调用方只管把当前预览的模板丢进来 */
  tpl: AnyVideoTemplate | null
  onClose: () => void
  /** 「用这个模板」：交给调用方去铺参数并关掉抽屉 */
  onUse: (t: AnyVideoTemplate) => void
  /**
   * 「生成案例片」—— **有它 = 自存模板没有成片时多一颗按钮**（没有就不渲染）。
   *
   * 自存模板存下来时可能还没有同比例的成片（见 `sampleUrl` 的注释），预览就只剩
   * 逐格播分镜。这颗按钮让用户当场用**当前表单的产品与卖点**跑一条这条模板的成片，
   * 完成后由调用方挂回模板（`sampleUrl`）——下一次预览就有片可播了。
   *
   * ⚠️ 为什么是**回调**而不是抽屉自己发任务：出片要的上下文（模型、产品、卖点、
   * 报价、任务中心）全在发起页，抽屉只是预览壳，拿不到也不该管。发起页给不出
   * 上下文时（比如从模板中心打开、没有表单）不传它，按钮自然不出现。
   */
  onGenerateSample?: (t: AnyVideoTemplate) => void
  /**
   * 这条模板**推荐/会铺开的生成引擎**（人话名字，如「Google Omni」）。
   *
   * 由调用方查好再传：抽屉不认识模型清单（它连 `useModels` 都不该用 ——
   * 模板中心那个入口没有表单上下文，多一个依赖就多一处可能取不到值）。
   * 空 = 自存模板（没有推荐引擎）→ 规格那栏不渲染「生成引擎」这一行。
   */
  engineName?: string
}) {
  const tplId = tpl?.id ?? ''
  /**
   * 片源两选一（自存模板优先播自己的成片）：
   *   · `sampleUrl` = 用户存这条模板时本页最近那条**真实成片**（用户口径
   *     「内容是生成的视频」）—— 演的是"你这套参数实际拍出来长什么样"；
   *   · 否则回落内置模板那套案例片 `public/templates/<id>.mp4`。
   * 后者对自存模板**必然不存在**（id 是 `vmt_` 开头、脚本不会给它生片），
   * 所以顺序不能反 —— 反了就是每次都先去要一个不存在的文件。
   */
  const sample = tpl?.sampleUrl ?? ''
  const videoSrc = tplId ? sample || `/templates/${tplId}.mp4` : ''

  const frames = useMemo(
    () => (tpl ? videoStoryboard(tpl.preset.duration, tpl.storyboard) : []),
    [tpl],
  )

  const [i, setI] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [videoReady, setVideoReady] = useState(false)
  const [vt, setVt] = useState(0)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  // 约束 3：换一条模板就回到第一帧重播，并把上一段的视频进度一起清掉
  useEffect(() => {
    setI(0)
    setPlaying(true)
    setVt(0)
  }, [tplId])

  // 约束 4：探这条模板有没有案例片。自存模板带 `sampleUrl` —— 它自己的成片就是片源，
  // 直接播（见文件头那段），探活留给内置模板那套「源码里不知道有没有」的案例片。
  useEffect(() => {
    if (!tplId) {
      setVideoReady(false)
      return
    }
    if (sample) {
      setVideoReady(true)
      return
    }
    /*
      自存模板**没有 `sampleUrl` 就到此为止**，不去探 `/templates/<id>.mp4`。
      那个路径只由 `gen-template-videos.py` 给**内置**模板生成；自存模板的 id 是
      `vmt_` 开头，HEAD 必然 404 —— 白白多发一次请求、控制台多一条红。

      这条路径在 2026-10-08 之后会**常态触发**：`sampleVideoUrl` 现在按比例过滤
      （见 VideoStudio 那段注释），而 `fix_template_samples.py` 又把存量那条比例不符的
      示例片清空了。也就是说「自存模板 + 没有示例片」是**正常状态**，不是探活失败 ——
      所以它该在探活之前被认出来，而不是靠 404 兜到同一个结果。
    */
    if (tpl?.custom) {
      setVideoReady(false)
      return
    }
    let alive = true
    setVideoReady(false)
    fetch(videoSrc, { method: 'HEAD' })
      .then((r) => {
        if (!alive) return
        const ct = r.headers.get('content-type') ?? ''
        setVideoReady(r.ok && !ct.startsWith('text/'))
      })
      .catch(() => {
        if (alive) setVideoReady(false)
      })
    return () => {
      alive = false
    }
  }, [tplId, videoSrc, sample])

  // 【分镜逐格】形态的推进器。播案例片时这条不跑（播放交给 <video> 自己）
  const fallback = frames.length && tpl ? (tpl.preset.duration * 1000) / frames.length : 1000

  useEffect(() => {
    if (!tpl || videoReady || !playing || !frames.length) return
    const ms = frameMs(frames[i]?.t ?? '', fallback)
    const id = window.setTimeout(() => {
      if (i + 1 < frames.length) setI(i + 1)
      // 放到最后一帧就停住，不循环 —— 循环播会让人以为「还没看完」
      else setPlaying(false)
    }, ms)
    return () => window.clearTimeout(id)
  }, [tpl, videoReady, playing, i, frames, fallback])

  if (!tpl) return null

  const idx = Math.min(i, frames.length - 1)
  const cur = frames[idx]
  const mode = getVideoMode(tpl.modes[0])
  const playable = Math.min(frames.length, CELLS)
  const progress = frames.length > 1 ? ((idx + 1) / frames.length) * 100 : 100

  /**
   * 播案例片时，「当前是第几条分镜」由播放头反推 —— 仍然用同一份帧列表，
   * 所以画面的位置和列表说的位置不会各说各话。
   */
  let vIdx = 0
  for (let n = 0; n < frames.length; n++) if (startSec(frames[n].t) <= vt + 0.15) vIdx = n
  const activeIdx = videoReady ? vIdx : idx
  const active = frames[activeIdx]

  const goShot = (k: number) => {
    if (videoReady) {
      const v = videoRef.current
      if (!v) return
      v.currentTime = startSec(frames[k].t) + 0.02
      void v.play().catch(() => {})
      return
    }
    setI(k)
    setPlaying(false)
  }

  return (
    <Drawer
      open
      onClose={onClose}
      icon="film"
      width={760}
      title={`模板预览 · ${templateTitle(tpl)}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            关闭
          </Button>
          <Button icon="check" onClick={() => onUse(tpl)}>
            用这个模板
          </Button>
        </>
      }
    >
      <div className="tprev">
        <div className="tprev__player">
          {videoReady ? (
            <div
              className="tprev__video"
              style={{
                aspectRatio: ratioCss(tpl.preset.aspectRatio),
                /* 宽度反推要用的比例（宽/高），见 `.tprev__video` 的注释 */
                ['--tprev-ar' as string]: String(ratioNum(tpl.preset.aspectRatio)),
              }}
            >
              <video
                ref={videoRef}
                src={videoSrc}
                controls
                autoPlay
                playsInline
                preload="metadata"
                onTimeUpdate={(e) => setVt(e.currentTarget.currentTime)}
                // 第二道网：探活说在、真播却挂（文件损坏 / 编码放不出来）就回落到逐格播
                onError={() => setVideoReady(false)}
              />
              {/*
                案例片形态**不打角标**（2026-10-10 用户口径：「不需要案例片这几个字了」）。
                标签压在成片上只是噪音 —— 画面本身就该说明问题。
                ⚠️ 逐格播那条时间戳角标（`.tprev__stamp`）是另一回事，照旧保留。
              */}
            </div>
          ) : (
            <>
              {/* key 带上帧号：换帧就重挂一次，靠 CSS 动画做出切镜头的顿挫 */}
              <div
                key={`${tplId}-${idx}`}
                className="tprev__frame"
                style={cellStyle(tpl.cover, idx)}
              >
                <span className="tprev__stamp">{cur?.t}</span>
              </div>

              <div className="tprev__ctl">
                <Button
                  variant="outline"
                  size="icon"
                  icon={playing ? 'pause' : 'play'}
                  onClick={() => {
                    // 已经放到结尾时再按播放 = 从头重播
                    if (!playing && idx + 1 >= frames.length) setI(0)
                    setPlaying(!playing)
                  }}
                  aria-label={playing ? '暂停' : '播放'}
                />
                <div className="tprev__track">
                  <i style={{ width: `${progress}%` }} />
                </div>
                <span className="tprev__count">
                  {idx + 1} / {frames.length}
                </span>
              </div>

              <div className="tprev__cells">
                {Array.from({ length: CELLS }, (_, k) => (
                  <button
                    key={k}
                    type="button"
                    style={cellStyle(tpl.cover, k)}
                    data-on={k === idx}
                    disabled={k >= playable}
                    onClick={() => {
                      setI(k)
                      setPlaying(false)
                    }}
                    title={k < playable ? `第 ${k + 1} 个镜头` : '封面补充帧（不参与播放）'}
                    aria-label={k < playable ? `第 ${k + 1} 个镜头` : '封面补充帧'}
                  />
                ))}
              </div>
            </>
          )}

          <p className="tprev__caption">{active?.d}</p>

          {/*
            「生成案例片」—— 只在**自存模板 + 还没有成片 + 发起页给了回调**时出现。
            三个条件缺一不可：内置模板都有案例片（不需要）；已有成片的模板也没必要
            （`sampleUrl` 就是从这儿挂上去的）；没有回调说明发起页没有可直接用的
            产品上下文（例如从模板中心打开），点了也发不出任务。
          */}
          {onGenerateSample && tpl.custom && !tpl.sampleUrl ? (
            <div className="tprev__sample">
              <Button
                variant="outline"
                size="sm"
                icon="sparkles"
                onClick={() => onGenerateSample(tpl)}
              >
                生成案例片
              </Button>
              <span className="text-xs muted">
                用当前产品与卖点跑一条这条模板的成片，完成后自动挂到卡片上（下次预览就能播）。
              </span>
            </div>
          ) : null}
        </div>

        <div className="tprev__side">
          <dl className="tprev__specs">
            <dt>档位</dt>
            <dd>{mode.name}</dd>
            <dt>场景</dt>
            <dd>{tpl.preset.scene}</dd>
            <dt>风格</dt>
            <dd>{tpl.preset.style}</dd>
            <dt>时长</dt>
            <dd>{tpl.preset.duration}s</dd>
            <dt>比例</dt>
            <dd>{tpl.preset.aspectRatio}</dd>
            {/*
              「生成引擎」那行 2026-10-10 加。
              `preset` 里本来没有引擎 —— 但 `pickTemplate` 从这天起会**连引擎一起铺**
              （15 秒 / 1:1 的模板必须换到跑得动它的档上，否则时长会被夹回去、
              卡片当场脱钩）。而抽屉正是「用这个模板」那颗按钮所在的地方，
              换了什么都不说，用户回去会发现引擎被改过而不知道是谁改的。
              自存模板没有推荐引擎（`engineName` 为空）→ 整行不渲染，不留空 `dt`。
            */}
            {engineName ? (
              <>
                <dt>生成引擎</dt>
                <dd>{engineName}</dd>
              </>
            ) : null}
          </dl>

          <div className="tprev__list">
            {frames.map((f, k) => (
              <button
                key={`${f.t}-${k}`}
                type="button"
                className="tprev__shot"
                data-on={k === activeIdx}
                onClick={() => goShot(k)}
              >
                <span className="tprev__shot-t">{f.t}</span>
                <span className="tprev__shot-d">{f.d}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Drawer>
  )
}
